import { addToast } from '../features/ui/toastSlice';

const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAYS = [1000, 3000, 10000]; // exponential backoff: 1s, 3s, 10s

/**
 * Shared utility for fetching data with retry logic, race condition prevention, and toast notifications.
 *
 * @param {Object} config - Configuration object
 * @param {Function} config.fetchFn - The actual API call function (must return a promise)
 * @param {Function} config.actions - Object containing Redux actions:
 *   - setData: Action to dispatch on success
 *   - setLoading: Action to dispatch to set loading state
 *   - setFailed: (Optional) Action to set failure state (boolean)
 *   - setError: (Optional) Action to set error message (null on start)
 *   - incrementRetry: Action to increment retry count
 * @param {Object} config.selectors - Object containing Redux selectors:
 *   - selectRetryCount: Selector for current retry count
 *   - selectLoading: Selector for loading state
 * @param {string} config.retryActionName - The string identifier for the toast retry action (e.g., 'RETRY_INTERSECTIONS')
 * @param {string} config.errorMessagePrefix - Prefix for the error message in the toast
 * @param {Function} config.retryThunk - The thunk to dispatch for retrying (must accept forceRetry boolean)
 * @param {Function} [config.shouldRetry] - Optional callback (getState) => boolean. If returns false, retry is cancelled.
 * @param {boolean} [forceRetry=false] - Whether to force the retry (bypass concurrency checks)
 * @returns {Function} Redux thunk
 */
export const fetchWithRetry = ({
    fetchFn,
    actions,
    selectors,
    retryActionName,
    errorMessagePrefix,
    retryThunk,
    shouldRetry,
}, forceRetry = false) => (dispatch, getState) => {
    const state = getState();
    const currentRetryCount = selectors.selectRetryCount(state);
    const isLoading = selectors.selectLoading(state);

    // If loading, don't fetch (prevent race conditions)
    if (isLoading) {
        return;
    }

    // If we've exceeded max retries and not forcing, don't retry
    if (!forceRetry && currentRetryCount > MAX_RETRY_ATTEMPTS) {
        return;
    }

    // If we are in the middle of a retry sequence (waiting for timeout) and not forcing, don't retry
    if (!forceRetry && currentRetryCount > 0) {
        return;
    }

    dispatch(actions.setLoading(true));
    if (actions.setError) dispatch(actions.setError(null)); // Clear previous error if applicable

    return fetchFn().then(
        response => {
            dispatch(actions.setData(response.data));
            dispatch(actions.setLoading(false));
            if (actions.setFailed) dispatch(actions.setFailed(false));
        },
        error => {
            console.error(`${errorMessagePrefix} error:`, error.message);
            dispatch(actions.incrementRetry());
            if (actions.setFailed) dispatch(actions.setFailed(true));
            dispatch(actions.setLoading(false));

            const newRetryCount = currentRetryCount + 1;

            if (newRetryCount <= MAX_RETRY_ATTEMPTS) {
                const delay = RETRY_DELAYS[newRetryCount - 1] || RETRY_DELAYS[RETRY_DELAYS.length - 1];
                console.log(`Retrying ${errorMessagePrefix.toLowerCase()} in ${delay}ms (attempt ${newRetryCount}/${MAX_RETRY_ATTEMPTS})...`);

                setTimeout(() => {
                    // Check if we should still retry based on current state
                    if (shouldRetry && !shouldRetry(getState())) {
                        console.log(`Retry for ${errorMessagePrefix} cancelled by shouldRetry check.`);
                        dispatch(actions.setLoading(false));
                        return;
                    }
                    dispatch(retryThunk(true));
                }, delay);
            } else {
                const errorMessage = error.response
                    ? `Server error (${error.response.status})`
                    : `Cannot connect to backend`;

                if (actions.setError) dispatch(actions.setError(errorMessage));

                const currentState = getState();
                const isDrivingMode = currentState.map?.isDrivingMode;
                const toastDuration = isDrivingMode ? 10000 : undefined; // 10s in driving mode, default otherwise

                dispatch(addToast({
                    message: `${errorMessagePrefix}: ${errorMessage}`,
                    type: 'error',
                    retryAction: retryActionName,
                    duration: toastDuration,
                }));

                console.error(`Max retry attempts (${MAX_RETRY_ATTEMPTS}) reached.`);
            }
        }
    );
};
